import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import AppLayout from "../components/AppLayout";
import { ErrorNote } from "../components/Feedback";
import { Btn, Card, Field, Input } from "../components/Shared";
import { SuccessNote } from "./admin/parts";
import { ProfileForm } from "./ProfilePage";
import ConfirmationsCard from "./ConfirmationsCard";

// /account — the signed-in user's own settings: who they are in the portal, their profile
// (first and last name, institute, address; the e-mail read-only: ProfilePage.jsx ProfileForm,
// PATCH /api/me), their ORCID iD
// (PATCH /api/me {orcid}; the server checks the check digit and answers the bare iD, which then
// fills the agent of an analysis they record). The eLabFTW API keys went on 2026-10-20 (task
// "protocols are files": a protocol is a file uploaded into a dataset).
// A curator (the account permission) also sees "Accounts waiting for you" (ConfirmationsCard, GB):
// the new accounts that named them to confirm them; the notification links to /account#confirmations.

function OrcidForm() {
  const { user, updateUser } = useAuth();
  const [value, setValue] = useState(user.orcid || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const save = async e => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const me = await api.patch("/me", { orcid: value.trim() || null });
      updateUser(me);
      setValue(me.orcid || "");
      setNotice(me.orcid ? "Your ORCID iD is saved." : "Your ORCID iD is removed.");
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} aria-label="Your ORCID iD" style={{ marginTop: 16 }}>
      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="ORCID iD" style={{ flex: "1 1 260px" }}
               hint="e.g. 0000-0002-1825-0097 or its orcid.org link. It fills the agent of the analyses you record.">
          <Input value={value} onChange={e => setValue(e.target.value)} spellCheck={false} autoComplete="off" />
        </Field>
        <Btn type="submit" disabled={busy}>{busy ? "Saving…" : "Save ORCID iD"}</Btn>
      </div>
    </form>
  );
}

function Profile() {
  const { user } = useAuth();
  if (!user) return null;
  const rows = [
    ["Username", user.username],
    ["Projects", (user.memberships || []).length
      ? <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {user.memberships.map(m => (
            <li key={m.project_id}><Link to={`/projects/${m.project_id}`}>{m.project_name}</Link> ({m.role})</li>
          ))}
        </ul>
      : "none yet"],
    ["Permissions", (user.roles || []).join(", ") || "none"],
    ["Signed in with", user.amr === "local" ? "a portal password" : "Keycloak"],
  ];
  return (
    <Card style={{ marginBottom: 20 }}>
      <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Your account</h2>
      <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 16px", margin: 0, fontSize: 14 }}>
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} style={{ display: "contents" }}>
            <dt style={{ color: "#4b5563" }}>{k}</dt>
            <dd style={{ margin: 0 }}>{v}</dd>
          </div>
        ))}
      </dl>
      <p style={{ margin: "12px 0 0", fontSize: 12, color: "#4b5563" }}>
        Permissions (admin, curator: may create projects) are given by a portal administrator;
        your role in a project (curator or user) by that project's curators.
      </p>
      <h3 style={{ margin: "20px 0 10px", fontSize: 15, fontWeight: 700 }}>Profile</h3>
      <ProfileForm />
      <OrcidForm />
    </Card>
  );
}

export default function AccountPage() {
  const { user } = useAuth();
  // GB: only an account with the permission `curator` can be named to confirm a new account
  const curator = (user?.roles || []).includes("curator");
  return (
    <AppLayout title="Account" crumbs={[{ label: "Account" }]}>
      {curator && <ConfirmationsCard />}
      <Profile />
    </AppLayout>
  );
}
